import React, { useState } from 'react';
import styled from 'styled-components';
import FirebaseErrorPopup from '../components/FirebaseErrorPopup';
import { useFirebaseError } from '../hooks/useFirebaseError';

const TestContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  font-family: 'MapoDPP', sans-serif;
`;

const TestContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  color: #333;
  text-align: center;
  margin-bottom: 30px;
  font-size: 2.5rem;
  font-weight: 700;
`;

const Description = styled.p`
  color: #666;
  text-align: center;
  margin-bottom: 40px;
  font-size: 1.1rem;
  line-height: 1.6;
`;

const ButtonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
`;

const TestButton = styled.button<{ variant: 'primary' | 'secondary' | 'danger' | 'warning' }>`
  padding: 15px 25px;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'MapoDPP', sans-serif;
  
  ${({ variant }) => {
    switch (variant) {
      case 'primary':
        return `
          background: #007bff;
          color: white;
          &:hover { background: #0056b3; transform: translateY(-2px); }
        `;
      case 'secondary':
        return `
          background: #6c757d;
          color: white;
          &:hover { background: #545b62; transform: translateY(-2px); }
        `;
      case 'danger':
        return `
          background: #dc3545;
          color: white;
          &:hover { background: #c82333; transform: translateY(-2px); }
        `;
      case 'warning':
        return `
          background: #ffc107;
          color: #212529;
          &:hover { background: #e0a800; transform: translateY(-2px); }
        `;
    }
  }}
  
  &:active {
    transform: translateY(0);
  }
`;

const StatusCard = styled.div`
  background: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 10px;
  padding: 20px;
  margin-top: 20px;
`;

const StatusTitle = styled.h3`
  color: #495057;
  margin-bottom: 10px;
  font-size: 1.2rem;
`;

const StatusText = styled.p<{ isError: boolean }>`
  color: ${({ isError }) => isError ? '#dc3545' : '#28a745'};
  font-weight: 600;
  margin: 5px 0;
`;

const CodeBlock = styled.pre`
  background: #2d3748;
  color: #e2e8f0;
  padding: 15px;
  border-radius: 8px;
  overflow-x: auto;
  font-size: 0.9rem;
  margin: 10px 0;
`;

const FirebaseErrorTestPage: React.FC = () => {
  const { errorState, handleFirebaseError, hideError } = useFirebaseError();
  const [testHistory, setTestHistory] = useState<string[]>([]);

  const addToHistory = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setTestHistory(prev => [`[${timestamp}] ${message}`, ...prev.slice(0, 9)]);
  };

  const testNetworkError = () => {
    const mockError = {
      code: 'unavailable',
      message: '서버에 연결할 수 없습니다.'
    };
    handleFirebaseError(mockError);
    addToHistory('네트워크 오류 테스트 실행');
  };

  const testTimeoutError = () => {
    const mockError = {
      code: 'deadline-exceeded',
      message: '요청 시간이 초과되었습니다.'
    };
    handleFirebaseError(mockError);
    addToHistory('시간 초과 오류 테스트 실행');
  };

  const testPermissionError = () => {
    const mockError = {
      code: 'permission-denied',
      message: '접근 권한이 없습니다.'
    };
    handleFirebaseError(mockError);
    addToHistory('권한 오류 테스트 실행');
  };

  const testCustomError = () => {
    handleFirebaseError(null, '사용자 정의 에러 메시지입니다.');
    addToHistory('사용자 정의 오류 테스트 실행');
  };

  const testQuizError = () => {
    const mockError = {
      code: 'internal',
      message: '서버 내부 오류가 발생했습니다.'
    };
    handleFirebaseError(mockError, '퀴즈 데이터 저장 중 오류가 발생했습니다.');
    addToHistory('퀴즈 데이터 저장 오류 테스트 실행');
  };

  const testRegistrationError = () => {
    const mockError = {
      code: 'already-exists',
      message: '이미 존재하는 데이터입니다.'
    };
    handleFirebaseError(mockError, '사용자 등록 중 오류가 발생했습니다.');
    addToHistory('사용자 등록 오류 테스트 실행');
  };

  const testMessageError = () => {
    const mockError = {
      code: 'resource-exhausted',
      message: '서버 리소스가 부족합니다.'
    };
    handleFirebaseError(mockError, '메시지 저장 중 오류가 발생했습니다.');
    addToHistory('메시지 저장 오류 테스트 실행');
  };

  const testButtonClickError = () => {
    const mockError = {
      code: 'failed-precondition',
      message: '요청 조건이 맞지 않습니다.'
    };
    handleFirebaseError(mockError, '버튼 클릭 정보 저장 중 오류가 발생했습니다.');
    addToHistory('버튼 클릭 정보 저장 오류 테스트 실행');
  };

  const clearHistory = () => {
    setTestHistory([]);
  };

  return (
    <TestContainer>
      <TestContent>
        <Title>🔥 Firebase 에러 팝업 테스트</Title>

        <ButtonGrid>
          <TestButton variant="danger" onClick={testNetworkError}>
            🌐 네트워크 오류
          </TestButton>
          
          <TestButton variant="warning" onClick={testTimeoutError}>
            ⏰ 시간 초과 오류
          </TestButton>
          
          <TestButton variant="secondary" onClick={testPermissionError}>
            🚫 권한 오류
          </TestButton>
          
          <TestButton variant="primary" onClick={testCustomError}>
            📝 사용자 정의 오류
          </TestButton>
          
          <TestButton variant="danger" onClick={testQuizError}>
            🧩 퀴즈 데이터 오류
          </TestButton>
          
          <TestButton variant="warning" onClick={testRegistrationError}>
            👤 사용자 등록 오류
          </TestButton>
          
          <TestButton variant="secondary" onClick={testMessageError}>
            💬 메시지 저장 오류
          </TestButton>
          
          <TestButton variant="primary" onClick={testButtonClickError}>
            🖱️ 버튼 클릭 오류
          </TestButton>
        </ButtonGrid>

        <StatusCard>
          <StatusTitle>📊 테스트 상태</StatusTitle>
          <StatusText isError={errorState.isError}>
            현재 에러 상태: {errorState.isError ? '에러 발생 중' : '정상'}
          </StatusText>
          {errorState.isError && (
            <StatusText isError={true}>
              에러 메시지: {errorState.errorMessage}
            </StatusText>
          )}
        </StatusCard>

        <StatusCard>
          <StatusTitle>📝 테스트 히스토리</StatusTitle>
          {testHistory.length > 0 ? (
            <>
              {testHistory.map((entry, index) => (
                <StatusText key={index} isError={false}>
                  {entry}
                </StatusText>
              ))}
              <TestButton 
                variant="secondary" 
                onClick={clearHistory}
                style={{ marginTop: '10px', padding: '8px 16px', fontSize: '0.9rem' }}
              >
                🗑️ 히스토리 지우기
              </TestButton>
            </>
          ) : (
            <StatusText isError={false}>아직 테스트가 실행되지 않았습니다.</StatusText>
          )}
        </StatusCard>

        <StatusCard>
        </StatusCard>
      </TestContent>

      {/* Firebase 에러 팝업 */}
      <FirebaseErrorPopup
        isVisible={errorState.isError}
        onClose={hideError}
        message={errorState.errorMessage}
      />
    </TestContainer>
  );
};

export default FirebaseErrorTestPage;
